import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Target, Eye, Heart, Users, GraduationCap, Mail, Linkedin } from "lucide-react";
import Header from "@/components/Header";

const AboutUs = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isDataProtectionOpen, setIsDataProtectionOpen] = useState(false);
  const [isEssentialCookiesOpen, setIsEssentialCookiesOpen] = useState(false);

  const values = [
    {
      icon: Heart,
      title: "Quality",
      description: "Provide high-quality research that is thorough, accurate, original, and relevant, adhering to the highest standards.",
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    {
      icon: Target,
      title: "Affordability",
      description: "Ensure services remain affordable, acknowledging the resource constraints often faced by researchers.",
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: BookOpen,
      title: "Reliability",
      description: "Guarantee reliability in research methods and results to provide utmost confidence to customers.",
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: Eye,
      title: "Transparency",
      description: "Uphold and maintain impeccable ethical practices, sharing research data openly and transparently with clients.",
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  const expertise = [
    "Evidence-based Healthcare",
    "Systematic Reviews of Quantitative and Qualitative Studies",
    "Mixed-Methods Evidence Synthesis",
    "Qualitative Data Collection and Analysis",
    "Questionnaire Development and Cross-cultural Adaptation",
    "Development of Clinical Practice Guidelines",
    "Quality Appraisal of Research Studies",
    "Research Priority Setting",
    "Medical Education-related Studies",
    "Surveys",
    "Proofreading of Manuscripts for Journal Publications",
    "Drafting Data Management Plans",
    "Ethics Applications"
  ];

  const training = [
    "Research Methods",
    "Medical Education Workshops",
    "Systematic Reviews",
    "Meta-analysis",
    "Qualitative Research Methods and Data Analysis",
    "Clinical Practice Guideline Development",
    "Critical Appraisal of Journal Articles"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" >
            About EviDent Research Consultancy Ltd
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            EviDent is a research consultancy with extensive experience and a robust reputation in healthcare research. 
            Our consultants together hold over 40 years of experience in the fields of health research and medical education, 
            based in Plymouth, United Kingdom, rendering services globally.
          </p>
        </section>

        {/* Vision & Mission */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <Card className="bg-white border-l-4 border-l-blue-600">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Eye className="h-8 w-8 text-blue-600" />
                <CardTitle className="text-2xl" >
                  Our Vision
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                To be the premier provider of research solutions, setting the benchmark for Quality, Affordability, 
                Reliability, and Transparency in every project undertaken, thus empowering researchers and 
                decision-makers worldwide.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-l-4 border-l-teal-600">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Target className="h-8 w-8 text-teal-600" />
                <CardTitle className="text-2xl" >
                  Our Mission
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                At our core, we are dedicated to delivering excellence in research. Our mission is to offer high-quality, 
                original, and relevant research services that adhere to the highest standards while ensuring affordability 
                for all clients. We pledge unwavering reliability in our methods and results and are committed to ensuring 
                transparency by openly sharing research data, fostering trust, and ethical collaboration with our valued customers.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Our Values */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10" >
            Our Core Values
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.title} className="bg-white hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-full ${value.bgColor} flex items-center justify-center mb-4`}>
                      <Icon className={`h-7 w-7 ${value.color}`} />
                    </div>
                    <CardTitle className="text-lg">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Consultants */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10" >
            Our Consultants
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Dr. Sumanth */}
            <Card className="bg-white">
              <CardHeader>
                <div className="mb-4">
                  <img 
                    src="/images/dr_sumanth.jpg"
                    alt="Dr. Sumanth Kumbargere Nagraj"
                    className="w-48 h-48 object-contain rounded-lg mx-auto shadow-md"
                  />
                </div>
                <CardTitle className="text-xl text-center" >
                  Dr. Sumanth Kumbargere Nagraj
                </CardTitle>
                <p className="text-sm text-gray-600 text-center">
                  MDS, MSc (Evidence-based Healthcare), FAIMER Fellow, PhD
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Dr. Sumanth is an experienced researcher and evidence synthesis specialist with extensive involvement 
                  in guideline development and evidence-based healthcare. He has served as a member of several guideline 
                  committees and contributed to major national and international initiatives aimed at strengthening 
                  methodological standards in health decision making.
                </p>
                <p className="text-gray-700">
                  Dr. Sumanth has also delivered numerous training programmes in evidence-based research across India, 
                  Malaysia, Norway, and the United Kingdom. He has a strong track record in mentoring early-career 
                  researchers, particularly those beginning their journey in systematic reviews and evidence synthesis. 
                  For the past 25 years, he has guided numerous students and junior professionals in developing sound 
                  research methodology and strengthening their analytical and critical appraisal skills. His teaching 
                  covers systematic and scoping review methods, meta-analysis, guideline development processes, and core 
                  principles of evidence-based practice, reflecting his commitment to building global research capacity 
                  and advancing methodological excellence.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <a href="mailto:sumanthkumbargerenagraj@gmail.com" className="text-blue-600 hover:underline">
                      sumanthkumbargerenagraj@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Linkedin className="h-4 w-4 text-gray-500" />
                    <a 
                      href="https://www.linkedin.com/in/sumanth-kumbargere-n-dr-83197a100/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                  <div className="flex space-x-4 text-xs mt-3">
                    <a 
                      href="https://orcid.org/0000-0003-1798-3245" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      ORCID
                    </a>
                    <a 
                      href="https://www.researchgate.net/profile/Sumanth-Kumbargere-Nagraj" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      ResearchGate
                    </a>
                    <a 
                      href="https://www.plymouth.ac.uk/staff/sumanth-kumbargere-nagraj" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      University Profile
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dr. Prashanti */}
            <Card className="bg-white">
              <CardHeader>
                <div className="mb-4">
                  <img 
                    src="/images/dr_prashanti.jpg"
                    alt="Dr. Prashanti Eachempati"
                    className="w-48 h-48 object-contain rounded-lg mx-auto shadow-md"
                  />
                </div>
                <CardTitle className="text-xl text-center" >
                  Dr. Prashanti Eachempati
                </CardTitle>
                <p className="text-sm text-gray-600 text-center">
                  MDS, MSc (Health Professions Education), FAIMER Fellow, PhD
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Dr. Prashanti Eachempati is a Specialist Dentist, Systematic Reviewer, and Methodologist dedicated to 
                  advancing evidence-based healthcare and research education. She serves as Co-convenor of the Cochrane 
                  Priority Setting Methods Group and holds the position of Clinical Lecturer at Peninsula Dental School, 
                  University of Plymouth, United Kingdom. She brings extensive experience working on projects involving 
                  guideline development, health policy, and evidence-informed decision making, including close engagement 
                  with major European and international research initiatives.
                </p>
                <p className="text-gray-700">
                  Dr. Eachempati has delivered more than 40 workshops across medical education and evidence-based practice, 
                  including pedagogic training, personal effectiveness development, and a wide range of evidence-based 
                  healthcare topics include GRADE training. She has substantial experience in faculty development, having 
                  trained educators from diverse health professions in assessment, teaching and learning strategies, 
                  personal effectiveness, and research-focused skill enhancement.
                </p>
                <p className="text-gray-700">
                  She is also deeply committed to mentoring researchers at different stages of their careers. Dr. Eachempati 
                  has supported numerous early-career researchers in building methodological skills, navigating their research 
                  pathways, and strengthening their confidence in evidence-based inquiry, while also collaborating effectively 
                  with experienced researchers on advanced methodological and educational initiatives.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <a href="mailto:drprashantie@gmail.com" className="text-blue-600 hover:underline">
                      drprashantie@gmail.com
                    </a>
                  </div>
                  <div className="flex space-x-4 text-xs mt-3">
                    <a 
                      href="https://orcid.org/0000-0003-1263-7423" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      ORCID
                    </a>
                    <a 
                      href="https://www.researchgate.net/profile/Eachempati-Prashanti" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      ResearchGate
                    </a>
                    <a 
                      href="https://www.plymouth.ac.uk/staff/prashanti-eachempati" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      University Profile
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Areas of Expertise */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10" >
            Areas of Expertise
          </h3>
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {expertise.map((item) => (
                  <div key={item} className="flex items-start space-x-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Teaching and Training */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10" >
            Teaching & Training
          </h3>
          <Card className="bg-white mb-8">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {training.map((item) => (
                  <div key={item} className="flex items-start space-x-2">
                    <GraduationCap className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Workshop Gallery */}
          <div className="mt-8">
            <h4 className="text-2xl font-semibold text-center text-gray-900 mb-6" >
              Our Workshops in Action
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow bg-gray-100">
                <img 
                  src="/images/workshop1.JPG"
                  alt="Workshop session"
                  className="w-full h-64 object-contain"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop2.JPG"
                  alt="Training workshop"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow bg-gray-100">
                <img 
                  src="/images/workshop3.JPG"
                  alt="Research methodology workshop"
                  className="w-full h-64 object-contain"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop4.JPG"
                  alt="Interactive training session"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop5.JPEG"
                  alt="Professional workshop"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop6.JPG"
                  alt="Workshop training session"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow bg-gray-100">
                <img 
                  src="/images/workshop7.JPG"
                  alt="Educational workshop"
                  className="w-full h-64 object-contain"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop8.JPG"
                  alt="Research methodology training"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop9.JPG"
                  alt="Workshop facilitation"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop10.jpg"
                  alt="Workshop participants"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src="/images/workshop11.jpeg"
                  alt="Training session"
                  className="w-full h-64 object-cover"
                />
              </div>
              <figure className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow" role="img" aria-labelledby="workshop-img-12-caption">
                <img 
                  src="/images/workshop12.JPG"
                  alt="Group photograph of research training workshop participants and resource persons. A diverse group of approximately 15-20 people standing together in formal and traditional attire, including colorful saris and professional business wear. Participants are arranged in two rows with some seated in front and others standing behind, all smiling at the camera in what appears to be a conference or training venue."
                  className="w-full h-64 object-cover"
                  loading="lazy"
                  aria-describedby="workshop-img-12-description"
                />
                <figcaption id="workshop-img-12-caption" className="sr-only">
                  Research Training Workshop Group Photo
                </figcaption>
                <div id="workshop-img-12-description" className="sr-only">
                  A celebratory group photograph taken at the conclusion of a research methodology training workshop organized by EviDent Research Consultancy. The image captures the collaborative and inclusive nature of the training programs, showing participants from diverse professional and cultural backgrounds coming together to strengthen their research capacity. The mix of traditional and professional attire reflects the Global South context in which EviDent specializes.
                </div>
              </figure>
            </div>
          </div>
        </section>

        {/* Target Clients */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10" >
            Who We Serve
          </h3>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  "Research Teams",
                  "Other Research Consultancies",
                  "Guideline Groups",
                  "Universities",
                  "Non-profit Organizations",
                  "Post-graduates/PhD Fellows",
                  "Government Research Departments",
                  "Research Funders",
                  "Health Organizations",
                  "Independent Researchers"
                ].map((client) => (
                  <div key={client} className="flex items-center space-x-2 bg-white rounded-lg p-3 shadow-sm">
                    <Users className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{client}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-blue-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4" >
            Ready to Collaborate?
          </h3>
          <p className="text-lg mb-8 text-blue-100">
            Let us help you achieve your research goals with our expertise and dedication.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/")}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Get Started
          </Button>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center space-y-4">
            <img 
              src="/images/logo.png" 
              alt="EviDent Research Consultancy" 
              className="h-16 w-auto"
            />
            <div className="text-center space-y-2">
              <p className="text-gray-400">
                © 2025 EviDent Research Consultancy Ltd. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Company Number: 15983903
              </p>
              <button
                onClick={() => setIsDataProtectionOpen(true)}
                className="text-blue-400 hover:text-blue-300 underline text-sm transition-colors cursor-pointer"
              >
                Data Protection
              </button>
              <p className="text-gray-500 text-sm">
                4-5, Russell Court, Palace Street, Plymouth, England, PL1 2AS
              </p>
              <button
                onClick={() => setIsEssentialCookiesOpen(true)}
                className="text-gray-400 hover:text-gray-300 underline text-xs mt-2 transition-colors cursor-pointer"
              >
                This site uses essential cookies for authentication and secure payments.
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Essential Cookies Privacy Policy Modal */}
      <Dialog open={isEssentialCookiesOpen} onOpenChange={setIsEssentialCookiesOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Essential Cookies Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <p className="text-sm text-gray-600">Last updated: 17 November 2025</p>
            
            <p className="text-sm sm:text-base leading-relaxed">
              EVIDENT RESEARCH CONSULTANCY LTD ("we", "us", "our") uses only essential cookies on this website. 
              Essential cookies are necessary for the website to function securely and correctly, and they cannot 
              be switched off in our systems.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">What Are Essential Cookies?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies are small text files placed on your device to enable core website features such as 
              page navigation, security, loading times, session management, and accessibility. Without these cookies, 
              the website may not operate properly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Why We Use Essential Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">We use essential cookies to:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm sm:text-base">
              <li>enable basic site functionality;</li>
              <li>maintain the security and stability of the website;</li>
              <li>remember your session or preferences needed to deliver the service;</li>
              <li>ensure pages load efficiently on your device.</li>
            </ul>
            <p className="text-sm sm:text-base leading-relaxed mt-2">
              These cookies do not collect personal data for marketing purposes, analytics, profiling, or advertising.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Your Consent</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Because essential cookies are strictly necessary for the functioning of this website, they do not require 
              your consent under UK GDPR and the Privacy and Electronic Communications Regulations (PECR). They are 
              automatically placed when you visit the site.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">How Long Are Essential Cookies Stored?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies may be stored only for the duration of your session or for a short period afterwards, 
              depending on their purpose.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Managing Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              You may be able to block or delete essential cookies through your browser settings. However, doing so 
              may cause parts of the website to stop working correctly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Contact Us</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              If you have any questions about our use of essential cookies, please contact us at:{" "}
              <a href="mailto:sumanthkumbargerenagraj@gmail.com" className="text-blue-600 hover:text-blue-800 underline">
                sumanthkumbargerenagraj@gmail.com
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Protection Modal */}
      <Dialog open={isDataProtectionOpen} onOpenChange={setIsDataProtectionOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Data Protection & Cookie Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <h3 className="text-lg font-semibold text-gray-900">Data Protection</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We handle personal information, study data and manuscripts with strict confidentiality and in full 
              alignment with the EU and UK GDPR. We collect only what is necessary, apply pseudonymisation whenever 
              possible, secure data through robust technical and organisational measures, and retain it only for as 
              long as required. When we process data on behalf of clients, we follow their written instructions and 
              enter into appropriate data-processing terms.
            </p>
            <p className="text-sm sm:text-base leading-relaxed">
              If personal data ever needs to be transferred outside the UK or the European Economic Area, we will 
              only do so using legally approved safeguards. These may include an adequacy decision or the required 
              contractual protections, such as the EU Standard Contractual Clauses or the UK International Data 
              Transfer Agreement. Individuals can exercise their rights to access, rectification, erasure, 
              restriction, portability and objection at any time.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Cookies and Similar Technologies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We use cookies to enable essential functionality on our website. These cookies are strictly necessary 
              for the website to function properly and cannot be disabled.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies:</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>Session Management:</strong> We use a secure session cookie (session_token) to keep you 
                  logged in and protect your account. This cookie expires after 7 days of inactivity.
                </li>
                <li>
                  <strong>Authentication Services:</strong> When you use Google Sign In, PayPal, or Stripe, these 
                  services may set their own cookies necessary for secure authentication and payment processing.
                </li>
              </ul>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed">
              By using our services, you consent to the use of these essential cookies. You can manage cookies 
              through your browser settings, though disabling them may prevent you from accessing certain features 
              of our website.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AboutUs;
